import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Crown } from "lucide-react";
import { Reveal, Chapter } from "./Reveal";
import { fireConfetti, scrollToSection } from "../lib/site";
import { useBooking } from "../lib/booking";
import { useContent } from "../lib/content";

const INCLUSIONS = [
  "Professional attendant",
  "Custom backdrops",
  "Premium props collection",
  "Red or black carpet",
  "Instant digital sharing",
  "Full setup & takedown",
];

const STYLES = ["light", "gold", "dark"];

const PricingCard = ({ tier, index }) => {
  const style = tier.popular ? "gold" : STYLES[index % 3] === "gold" ? "light" : STYLES[index % 3];
  const dark = style === "dark";
  const gold = style === "gold";
  const { setPkg } = useBooking();
  const [tierIdx, setTierIdx] = useState(0);
  const hasTiers = tier.tiers?.length > 0;
  const activeTier = hasTiers ? tier.tiers[Math.min(tierIdx, tier.tiers.length - 1)] : null;
  return (
    <Reveal delay={index * 0.12} className="h-full">
      <motion.div
        whileHover={{ y: -8 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className={`card-glow-hover relative h-full flex flex-col rounded-3xl p-8 lg:p-10 ${
          dark
            ? "bg-[#3c003a] text-white shadow-[0_20px_60px_rgba(60,0,58,0.35)]"
            : gold
            ? "bg-white ring-2 ring-[#c9006f] shadow-[0_0_60px_rgba(201,0,111,0.3)]"
            : "bg-white shadow-[0_8px_30px_rgba(145,0,141,0.06)] border border-black/5"
        }`}
        data-testid={`package-card-${tier.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      >
        {tier.popular && (
          <span className="absolute -top-4 left-1/2 -translate-x-1/2 btn-gold rounded-full px-5 py-1.5 text-xs font-bold tracking-[0.15em] uppercase flex items-center gap-1.5">
            <Crown size={13} /> Most Popular
          </span>
        )}
        <p
          className={`uppercase tracking-[0.24em] text-xs font-semibold mb-3 ${
            dark ? "text-[#ffc4e4]" : "text-[#a3005a]"
          }`}
        >
          {tier.hours}
        </p>
        <h3 className={`font-serif text-3xl mb-2 ${dark ? "text-white" : "text-[#121212]"}`}>
          {tier.name}
        </h3>
        <p className={`text-sm mb-8 ${dark ? "text-white/60" : "text-[#121212]/55"}`}>
          {tier.tagline}
        </p>
        <div className="flex items-baseline gap-2 mb-8">
          <span className={`font-serif text-5xl ${dark ? "text-[#ffc4e4]" : "text-[#91008d]"}`}>
            ${(activeTier ? activeTier.price : tier.price).toLocaleString()}
          </span>
          <span className={`text-sm ${dark ? "text-white/50" : "text-[#121212]/45"}`}>
            / {activeTier ? activeTier.label.toLowerCase() : tier.price_suffix || tier.hours.toLowerCase()}
          </span>
        </div>
        {hasTiers && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid={`package-tier-picker-${tier.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            {tier.tiers.map((t, i) => (
              <button
                key={t.label}
                type="button"
                onClick={() => setTierIdx(i)}
                data-testid={`package-tier-btn-${i}`}
                className={`rounded-full px-4 py-2 text-xs font-bold transition-all duration-300 ${
                  i === tierIdx
                    ? "bg-[#c9006f] text-white shadow-[0_0_16px_rgba(201,0,111,0.4)]"
                    : dark
                    ? "border border-white/25 text-white/70 hover:border-[#c9006f] hover:text-[#ffc4e4]"
                    : "border border-[#91008d]/30 text-[#91008d] hover:border-[#c9006f] hover:text-[#c9006f]"
                }`}
              >
                ${t.price.toLocaleString()} · {t.label.replace(/^Up to /i, "≤")}
              </button>
            ))}
          </div>
        )}
        <ul className="space-y-3.5 mb-10 flex-1">
          {(tier.features?.length ? tier.features : INCLUSIONS).map((inc) => (
            <li key={inc} className="flex items-start gap-3 text-sm">
              <span
                className={`mt-0.5 grid place-items-center w-5 h-5 rounded-full shrink-0 ${
                  dark ? "bg-[#c9006f]/20 text-[#ffc4e4]" : "bg-[#91008d]/10 text-[#91008d]"
                }`}
              >
                <Check size={12} strokeWidth={3} />
              </span>
              <span className={dark ? "text-white/80" : "text-[#121212]/75"}>{inc}</span>
            </li>
          ))}
        </ul>
        <button
          onClick={(e) => {
            setPkg(
              activeTier
                ? `${tier.name} (${activeTier.label}) — $${activeTier.price}`
                : `${tier.name} — $${tier.price}`
            );
            fireConfetti(e);
            scrollToSection("#contact");
          }}
          data-testid={`select-package-${tier.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={`w-full rounded-full py-4 text-sm font-bold tracking-wide transition-all duration-300 ${
            gold
              ? "btn-gold"
              : dark
              ? "bg-[#c9006f] text-[#3c003a] hover:bg-[#ffc4e4]"
              : "border-2 border-[#91008d] text-[#91008d] hover:bg-[#91008d] hover:text-white"
          }`}
        >
          Select Package
        </button>
      </motion.div>
    </Reveal>
  );
};

export const Packages = () => {
  const { packages } = useContent();
  return (
  <section id="packages" className="py-24 lg:py-32 bg-[#f8eef6] relative grain" data-testid="packages-section">
    <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
      <Chapter number="02" label="Our Packages" />
      <Reveal>
        <h2 className="font-serif text-[#121212] text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.1] mb-4">
          Choose your <em className="text-[#a3005a]">island tier</em>
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="text-[#121212]/60 text-base md:text-lg leading-[1.7] max-w-2xl mb-16">
          Named after Cayman's native flora — every package includes the complete
          GlamBooth white-glove treatment.
        </p>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-6 lg:gap-8 pt-4">
        {packages.map((tier, i) => (
          <PricingCard key={tier.id || tier.name} tier={tier} index={i} />
        ))}
      </div>
    </div>
  </section>
  );
};
